// Copyright (c) Subzero Labs, Inc.
// SPDX-License-Identifier: Apache-2.0

import type { Wallet, WalletAccount } from "@wallet-standard/base";
import type { StandardConnectFeature, StandardDisconnectFeature } from "@wallet-standard/features";
import type { FrostConfig } from "../config";
import {
  ConnectionFailedError,
  isFrostError,
  UnsupportedFeatureError,
  WalletError,
  WalletNotFoundError,
} from "../errors";
import type { ConnectionStatus } from "../types";

type WalletWithConnect = Wallet & {
  features: {
    "standard:connect"?: StandardConnectFeature["standard:connect"];
    "standard:disconnect"?: StandardDisconnectFeature["standard:disconnect"];
  };
};

export interface ActiveConnection {
  /** Current connection status */
  status: ConnectionStatus;
  /** Target chain ID */
  chainId: string;
  /** Connected wallet name (null when disconnected) */
  walletName: string | null;
  /** Connected account address (null when disconnected) */
  accountAddress: string | null;
  /** Timestamp (epoch Date.now()) when connection was established */
  connectedAt: number | null;
  /** Timestamp (epoch Date.now()) of last user activity */
  lastUsedAt: number;
}

/**
 * Gets the current connection state synchronously.
 *
 * @param config - Frost configuration
 * @returns Current connection status, wallet, and account info
 */
export function getActiveConnection(config: FrostConfig): ActiveConnection {
  const state = config.store.state;
  return {
    status: state.status,
    chainId: state.chainId,
    walletName: state.walletName,
    accountAddress: state.accountAddress,
    connectedAt: state.connectedAt,
    lastUsedAt: state.lastUsedAt,
  };
}

export interface ConnectOptions {
  /** Name of the wallet to connect */
  walletName: string;
  /** Attempt silent connection (no popup) - for reconnection */
  silent?: boolean;
}

export interface ConnectResult {
  /** Name of the connected wallet */
  walletName: string;
  /** Address of the connected account */
  accountAddress: string;
}

/**
 * Connection mutex to prevent race conditions when connect() is called multiple times.
 * Maps config -> pending connection promise.
 * Uses WeakMap to avoid memory leaks.
 */
const pendingConnections: WeakMap<FrostConfig, Map<string, Promise<ConnectResult>>> = new WeakMap();

/**
 * Connects to a wallet.
 *
 * @param config - Frost configuration
 * @param options - Connection options including wallet name
 * @returns Connected wallet name and account address
 * @throws {WalletNotFoundError} If wallet is not registered
 * @throws {UnsupportedFeatureError} If wallet doesn't support connect
 * @throws {ConnectionFailedError} If connection fails or no accounts returned
 */
export async function connect(
  config: FrostConfig,
  options: ConnectOptions
): Promise<ConnectResult> {
  let walletConnections = pendingConnections.get(config);
  if (!walletConnections) {
    walletConnections = new Map();
    pendingConnections.set(config, walletConnections);
  }

  // Check if there's already a pending connection for THIS wallet
  const existingConnection = walletConnections.get(options.walletName);
  if (existingConnection) {
    return existingConnection; // Return same promise for deduplication
  }

  const connectionPromise = connectInternal(config, options);
  walletConnections.set(options.walletName, connectionPromise);

  try {
    return await connectionPromise;
  } finally {
    walletConnections.delete(options.walletName);
  }
}

/** Internal connect implementation */
async function connectInternal(
  config: FrostConfig,
  options: ConnectOptions
): Promise<ConnectResult> {
  const { walletName, silent = false } = options;
  const { store, options: configOpts } = config;

  // Get wallet reference
  const wallet = store.state._walletRefs.get(walletName) as WalletWithConnect | undefined;
  if (!wallet) {
    throw new WalletNotFoundError(walletName);
  }

  // Update status to connecting
  store.setState(state => ({
    ...state,
    status: "connecting",
    walletName,
  }));

  try {
    // Connect via wallet-standard
    const connectFeature = wallet.features["standard:connect"];
    if (!connectFeature) {
      throw new UnsupportedFeatureError("standard:connect", walletName);
    }

    const result = await connectFeature.connect({ silent });

    if (!result.accounts.length) {
      throw new ConnectionFailedError("No accounts returned", walletName);
    }

    const account: WalletAccount = result.accounts[0];
    const now = Date.now();

    // Update state with connection
    store.setState(state => {
      const accounts = new Map(state.accounts);
      // Store the raw WalletAccount reference inside AccountEntity for wallet-standard operations
      accounts.set(account.address, {
        address: account.address,
        publicKey: new Uint8Array(account.publicKey),
        walletName,
        chains: [...account.chains],
        _raw: account,
      });

      const wallets = new Map(state.wallets);
      const existingWallet = wallets.get(walletName);
      if (existingWallet) {
        wallets.set(walletName, {
          ...existingWallet,
          lastConnectedAt: now,
        });
      }

      return {
        ...state,
        status: "connected",
        walletName,
        accountAddress: account.address,
        connectedAt: now,
        sessionExpiresAt: now + configOpts.sessionTTL,
        lastUsedAt: now,
        userDisconnected: false, // Clear the flag on successful connect
        accounts,
        wallets,
      };
    });

    // Subscribe to wallet events
    config._bridge?.subscribeToWallet(wallet);

    return {
      walletName,
      accountAddress: account.address,
    };
  } catch (error) {
    // Reset state on failure
    store.setState(state => ({
      ...state,
      status: "disconnected",
      walletName: null,
      accountAddress: null,
      connectedAt: null,
      sessionExpiresAt: null,
    }));

    // Re-throw FrostErrors as-is, wrap others
    if (isFrostError(error)) {
      throw error;
    }
    throw new WalletError(error, walletName);
  }
}

/**
 * Disconnects from the current wallet.
 *
 * Clears connection state and sets `userDisconnected` to prevent auto-reconnect.
 *
 * @param config - Frost configuration
 */
export async function disconnect(config: FrostConfig): Promise<void> {
  const { store } = config;
  const { walletName } = store.state;

  if (!walletName) return;

  const wallet = store.state._walletRefs.get(walletName) as WalletWithConnect | undefined;

  // Unsubscribe from wallet events
  config._bridge?.unsubscribeFromWallet(walletName);

  // Try to disconnect via wallet-standard
  try {
    const disconnectFeature = wallet?.features["standard:disconnect"];
    if (disconnectFeature) {
      await disconnectFeature.disconnect();
    }
  } catch (error) {
    // Log but don't throw - wallet might already be disconnected
    console.warn("[frost] Wallet disconnect failed:", error);
  }

  // Clear connection state (keep wallets for quick reconnect)
  // Clear accounts as their _raw references are no longer valid after disconnect
  // Set userDisconnected to prevent auto-reconnect
  store.setState(state => ({
    ...state,
    status: "disconnected",
    walletName: null,
    accountAddress: null,
    connectedAt: null,
    sessionExpiresAt: null,
    userDisconnected: true,
    accounts: new Map(),
  }));
}

/**
 * Clears any pending connection promises for the given config.
 * Called during config.destroy() to clean up resources.
 *
 * @param config - Frost configuration
 * @internal
 */
export function clearPendingConnections(config: FrostConfig): void {
  const walletConnections = pendingConnections.get(config);
  if (walletConnections) {
    walletConnections.clear();
    pendingConnections.delete(config);
  }
}

/**
 * Attempts silent reconnection to previously connected wallet.
 *
 * @param config - Frost configuration
 * @returns Connection result if successful, null if reconnection not possible
 */
export async function reconnect(config: FrostConfig): Promise<ConnectResult | null> {
  const { store } = config;
  const { walletName, sessionExpiresAt, userDisconnected } = store.state;

  // No previous connection
  if (!walletName) return null;

  // User explicitly disconnected - don't auto-reconnect
  if (userDisconnected) return null;

  // Session expired
  if (sessionExpiresAt && sessionExpiresAt < Date.now()) {
    store.setState(state => ({
      ...state,
      status: "disconnected",
      walletName: null,
      accountAddress: null,
      connectedAt: null,
      sessionExpiresAt: null,
    }));
    return null;
  }

  // Update status
  store.setState(state => ({ ...state, status: "reconnecting" }));

  try {
    // Wait for wallet to be discovered
    const wallet = await config._registry?.waitForWallet(walletName, { timeout: 5000 });

    if (!wallet) {
      throw new Error("Wallet not found");
    }

    return await connect(config, { walletName, silent: true });
  } catch {
    // Silent reconnect failed - reset to disconnected
    store.setState(state => ({
      ...state,
      status: "disconnected",
      walletName: null,
      accountAddress: null,
      connectedAt: null,
      sessionExpiresAt: null,
    }));
    return null;
  }
}
